import { Box, Flex, Heading, Text, Button, VStack, Toaster, createToaster } from '@chakra-ui/react'
import { Plus, BarChart3, Wallet, FileText, Building, Users, Settings, Gauge, Truck } from 'lucide-react'
import { NavLink, Outlet, useParams } from 'react-router-dom'
import { useProperty } from '@/features/properties/hooks/useProperties'
import { FiscalYearCreateModal } from '../components'
import { AccountingProvider, useAccountingContext } from '../context/AccountingContext'

const toaster = createToaster({
  placement: 'top-end',
  duration: 4000,
})

export { toaster as accountingToaster }

const NAV_ITEMS = [
  { to: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
  { to: 'budgets', label: 'Budget & Appels', icon: Wallet },
  { to: 'meter-readings', label: 'Relevés', icon: Gauge },
  { to: 'operations', label: 'Opérations', icon: FileText },
  { to: 'bank', label: 'Banque', icon: Building },
  { to: 'owners', label: 'Comptes copropriétaires', icon: Users },
  { to: 'suppliers', label: 'Fournisseurs', icon: Truck },
  { to: 'reports', label: 'États financiers', icon: BarChart3 },
  { to: 'settings', label: 'Paramétrage', icon: Settings },
]

function AccountingLayoutContent() {
  const { residenceId } = useParams<{ residenceId: string }>()
  const { data: residence, isLoading: residenceLoading } = useProperty(residenceId)
  const { activeFiscalYear, isFiscalYearModalOpen, setIsFiscalYearModalOpen } = useAccountingContext()

  if (residenceLoading) {
    return (
      <Box p={6}>
        <Text>Chargement...</Text>
      </Box>
    )
  }

  if (!residenceId || !residence) {
    return (
      <Box p={6}>
        <Text>Résidence non trouvée</Text>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="start" mb={6}>
        <Box>
          <Heading size="xl" mb={1}>
            Comptabilité
          </Heading>
          <Text color="gray.600">{residence.name}</Text>
        </Box>
      </Flex>

      {/* Warning si pas d'exercice */}
      {!activeFiscalYear && (
        <Box
          mb={6}
          p={4}
          bg="orange.50"
          borderRadius="lg"
          borderLeft="4px solid"
          borderColor="orange.400"
        >
          <Flex align="center" gap={3}>
            <Box color="orange.500">
              <BarChart3 size={24} />
            </Box>
            <Box flex={1}>
              <Text fontWeight="medium" color="orange.800">
                Aucun exercice comptable
              </Text>
              <Text fontSize="sm" color="orange.600">
                Créez un exercice comptable pour commencer à utiliser le module comptabilité.
              </Text>
            </Box>
            <Button colorPalette="orange" onClick={() => setIsFiscalYearModalOpen(true)}>
              <Plus size={16} />
              Créer un exercice
            </Button>
          </Flex>
        </Box>
      )}

      {/* Navigation tabs */}
      <Flex
        gap={0}
        mb={6}
        borderBottom="1px solid"
        borderColor="gray.200"
        overflowX="auto"
      >
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <Flex
                align="center"
                gap={2}
                px={4}
                py={3}
                color={isActive ? 'brand.600' : 'gray.600'}
                borderBottom="2px solid"
                borderColor={isActive ? 'brand.600' : 'transparent'}
                fontWeight={isActive ? 'semibold' : 'normal'}
                _hover={{ color: 'brand.600', bg: 'gray.50' }}
                transition="all 0.2s"
                whiteSpace="nowrap"
              >
                <Icon size={16} />
                <Text fontSize="sm">{label}</Text>
              </Flex>
            )}
          </NavLink>
        ))}
      </Flex>

      {/* Content */}
      <Outlet />

      <FiscalYearCreateModal
        isOpen={isFiscalYearModalOpen}
        onClose={() => setIsFiscalYearModalOpen(false)}
        residenceId={residenceId}
      />

      {/* Toaster */}
      <Toaster toaster={toaster}>
        {(toast) => (
          <Box
            bg={toast.type === 'success' ? 'green.500' : toast.type === 'error' ? 'red.500' : 'blue.500'}
            color="white"
            p={4}
            borderRadius="md"
            boxShadow="lg"
          >
            <VStack align="start" gap={1}>
              {toast.title && <Text fontWeight="bold">{toast.title}</Text>}
              {toast.description && <Text fontSize="sm">{toast.description}</Text>}
            </VStack>
          </Box>
        )}
      </Toaster>
    </Box>
  )
}

export function AccountingLayout() {
  const { residenceId } = useParams<{ residenceId: string }>()

  if (!residenceId) {
    return (
      <Box p={6}>
        <Text>Résidence non spécifiée</Text>
      </Box>
    )
  }

  return (
    <AccountingProvider residenceId={residenceId}>
      <AccountingLayoutContent />
    </AccountingProvider>
  )
}
